/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { type ValidationErrors } from '@angular/forms';
import { type ColumnDef, type Rest, type TableConfig, DMValidators, FormControl, FormGroup, SlideWindowAbstract } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { ToolService } from 'app/common/services';
import { type UnixLocalUser, type WindowsLocalUser } from 'app/common/types/mo';
import { ProductConfig } from 'app/config';

import { GuideItemE } from '../../../file-system/file-system-guide/file-system-guide.service';
import { FsQuotaNameTypeE, FsQuotaTypeE } from '../../user-quota.const';
import { type UserOrGroup, DomainTypeE, NameTypeE, UserOrGroupTypeE, UserOsTypeE } from '../user-quota';
import { UserQuotaService } from '../user-quota.service';
import { UserQuotaStrService } from '../user-quota-str.service';

type User = UnixLocalUser & WindowsLocalUser;

@Component({
    selector: 'app-add-user-or-group',
    templateUrl: './add-user-or-group.component.html',
    styleUrls: ['./add-user-or-group.component.scss'],
})
export class AddUserOrGroupComponent extends SlideWindowAbstract implements OnInit {
    /**
     * 选择用户还是用户组
     */
    @Input() type: 'user' | 'group';
    /**
     * 上一次选中的数据
     */
    @Input() data: UserOrGroup[];
    /**
     * 租户ID
     */
    @Input() vstoreId: string;
    /**
     * 配额类型
     */
    @Input() quotaType: FsQuotaTypeE;

    closeArgs: {
        refresh: boolean;
        data: UserOrGroup[];
    } = {
            refresh: false,
            data: null,
        };
    i18n = this.str.i18n;
    title: string;
    domainTypeLab: string;

    belongType = this.i18n.unixUserLab;
    unixDomainTypeOpts: SelectOption[];
    windowsDomainTypeOpts: SelectOption[];
    DomainTypeE = DomainTypeE;
    ProductConfig = ProductConfig;
    FsQuotaTypeE = FsQuotaTypeE;
    FsQuotaNameTypeE = FsQuotaNameTypeE;

    /**
     * 用户用户组类型
     */
    get unixDomainType(): DomainTypeE {
        return this.unixVm ? this.unixVm.value.domainType : DomainTypeE.Local;
    }
    get windowsDomainType(): DomainTypeE {
        return this.windowsVm ? this.windowsVm.value.domainType : DomainTypeE.Local;
    }

    unixRest: Rest;
    unixSelection: UnixLocalUser[] = [];
    unixTableConfig: TableConfig;
    unixTableColumns: ColumnDef[];

    windowsRest: Rest;
    windowsSelection: WindowsLocalUser[] = [];
    windowsTableConfig: TableConfig;
    windowsTableColumns: ColumnDef[];

    /**
     * 保存上一次输入的LDAP域名称
     */
    ldapNames: string[] = [];
    /**
     * 保存上一次输入的NIS域名称
     */
    nisNames: string[] = [];
    /**
     * 保存上一次输入的AD域名称
     */
    adNames: string[] = [];

    unixVm: FormGroup;
    unixErrorMessage: any;

    windowsVm: FormGroup;
    windowsErrorMessage: any;

    // 本地用户推选框 require message
    unixShowErrorMsg = false;
    WindowsShowErrorMsg = false;

    configGuide = null;
    showGuideTip = true;

    constructor(
        injector: Injector,
        public str: UserQuotaStrService,
        private service: UserQuotaService,
        private toolService: ToolService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        if (this.type === 'user') {
            this.title = this.i18n.addUserTitle;
            this.domainTypeLab = this.i18n.userTypeLab;

            this.unixDomainTypeOpts = this.str.unixUserDomainTypeOpts;
            this.windowsDomainTypeOpts = this.str.windowsUserDomainTypeOpts;

            this.unixRest = this.service.getUnixUserRest(this.vstoreId);
            this.windowsRest = this.service.batchWindowsUserRest(this.vstoreId);
        } else {
            this.title = this.i18n.addUserGroupTitle;
            this.domainTypeLab = this.i18n.userGroupTypeLab;

            this.unixDomainTypeOpts = this.str.unixUserGroupDomainTypeOpts;
            this.windowsDomainTypeOpts = this.str.windowsUserGroupDomainTypeOpts;

            this.unixRest = this.service.getUnixUserGroupRest(this.vstoreId);
            this.windowsRest = this.service.batchWindowsUserGroupRest(this.vstoreId);
        }

        this.initVm();
        this.initTable();

        if (this.type === FsQuotaNameTypeE.GROUP && this.quotaType === FsQuotaTypeE.GROUP) {
            this.windowsVm.patchValue({ domainType: DomainTypeE.AD });
        }

        this.windowVisible = true;
        this.pageReady = true;
    }

    initVm(): void {
        // unix
        this.unixVm = new FormGroup({
            domainType: new FormControl(this.unixDomainType),
            names: new FormControl([], [
                DMValidators.multiInputValidator(true, [
                    DMValidators.rangeByteLength(1, 128),
                ]),
            ]),
        });
        this.unixErrorMessage = {
            names: {
                required: this.toolService.requiredErr(this.i18n.nameLab),
                rangeByteLength: this.str.rangeByteOneLengthErr(1, 128),
            },
        };

        // windows
        this.windowsVm = new FormGroup({
            domainType: new FormControl(this.windowsDomainType),
            names: new FormControl([], [
                DMValidators.multiInputValidator(true, [
                    DMValidators.rangeByteLength(1, 256),
                    this.adNameFormatValidator,
                ]),
            ]),
        });
        this.windowsErrorMessage = {
            names: {
                required: this.toolService.requiredErr(this.i18n.nameLab),
                rangeByteLength: this.str.rangeByteOneLengthErr(1, 256),
                nameFormat: this.type === 'user' ? this.i18n.adUserNameFormatErr : this.i18n.adUserGroupNameFormatErr,
            },
        };

        this.initVmSubscribe();
    }

    /**
     * 初始化表单监听事件
     */
    initVmSubscribe(): void {
        // unix用户类型切换时，将当前输入值保存，将上一次保存的值取出
        this.unixVm.get('domainType').valueChanges.subscribe((value: DomainTypeE) => {
            const lastValue: DomainTypeE = this.unixVm.value.domainType;
            if (lastValue === value) {
                return;
            }

            const names = this.unixVm.value.names;
            let restoreNames = [];

            if (lastValue === DomainTypeE.LDAP) {
                this.ldapNames = names;
            } else if (lastValue === DomainTypeE.NIS) {
                this.nisNames = names;
            } else if (lastValue === DomainTypeE.AD) {
                this.adNames = names;
            }
            if (value === DomainTypeE.LDAP) {
                restoreNames = this.ldapNames;
            } else if (value === DomainTypeE.NIS) {
                restoreNames = this.nisNames;
            } else if (value === DomainTypeE.AD) {
                restoreNames = this.adNames;
            } else {
                //
            }

            this.unixVm.patchValue({ names: restoreNames });
        });
    }

    initTable(): void {
        // unix, root用户(id=0)禁止创建用户配额
        this.unixTableConfig = {
            dataKey: 'id',
            selectionMode: 'checkbox',
            checkRowDisabled: (data) => ({ disabled: Number(data.id) === 0, tip: this.i18n.rootErrorTip }),
        };
        this.unixTableColumns = [
            {
                header: this.i18n.nameLab,
                field: 'name',
                filterType: 'search',
            },
            {
                header: this.i18n.idLab,
                field: 'id',
                filterType: 'equalSearch',
                sortable: true,
            },
        ];
        // 用户添加主组列
        if (this.type === 'user') {
            this.unixTableColumns.push({
                header: this.i18n.primaryGroupLab,
                field: 'primary_group_name',
            });
        }

        // windows
        this.windowsTableConfig = {
            dataKey: 'RID',
            selectionMode: 'checkbox',
        };
        // windows本地用户、用户组table结构相同
        this.windowsTableColumns = [
            {
                header: this.i18n.nameLab,
                field: 'NAME',
                filterType: 'search',
                className: ['table-cell-text-pre'],
            },
            {
                header: this.i18n.ridLab,
                field: 'RID',
                filterType: 'search',
                sortable: true,
            },
        ];
    }

    initConfigGuide(domainType: DomainTypeE): void {
        if (domainType === DomainTypeE.AD) {
            this.configGuide = {
                guideItems: [GuideItemE.AD],
                textConfigs: [
                    {
                        label: this.i18nService.get('service.quota_term_create_addUser_configAD_info'),
                    },
                ],
            };
        } else if (domainType === DomainTypeE.NIS) {
            this.configGuide = {
                guideItems: [GuideItemE.NIS],
                textConfigs: [
                    {
                        label: this.i18nService.get('service.quota_term_create_addUser_configNIS_info'),
                    },
                ],
            };
        } else if (domainType === DomainTypeE.LDAP) {
            this.configGuide = {
                guideItems: [GuideItemE.LDAP],
                textConfigs: [
                    {
                        label: this.i18nService.get('service.quota_term_create_addUser_configLDAP_info'),
                    },
                ],
            };
        } else {
            //
        }
    }

    /**
     * 还原选中的数据
     */
    restoreData(): void {
        if (!this.data) {
            return;
        }

        this.data.forEach((a) => {
            // 本地用户/用户组
            if (a.DOMAINTYPE === DomainTypeE.Local) {
                this.unixSelection.push({
                    id: a.id as any,
                    name: a.USRGRPOWNERNAME,
                });
            } else if (a.DOMAINTYPE === DomainTypeE.LDAP) {
                this.ldapNames.push(a.USRGRPOWNERNAME);
            } else if (a.DOMAINTYPE === DomainTypeE.NIS) {
                this.nisNames.push(a.USRGRPOWNERNAME);
            } else if (a.DOMAINTYPE === DomainTypeE.AD) {
                this.adNames.push(a.USRGRPOWNERNAME);
            }
        });
    }

    /**
     * 选项变化时隐藏错误提示
     */
    onSelectionChange(): void {
        const { selection } = this.getCurrentValue();
        this.changeErrorMsg(selection.length === 0);
    }

    domainTypeChange(domainType: DomainTypeE): void {
        if ([DomainTypeE.AD, DomainTypeE.NIS, DomainTypeE.LDAP].includes(domainType)) {
            this.initConfigGuide(domainType);
        }
    }

    onTabChange(index: number): void {
        this.initConfigGuide([this.unixVm, this.windowsVm][index].value.domainType);
    }

    /**
     * 获取当前tab相关值
     */
    getCurrentValue(key = this.belongType): any {
        const unix = this.i18n.unixUserLab;
        const windows = this.i18n.windowsUserLab;

        const selection = {
            [unix]: this.unixSelection,
            [windows]: this.windowsSelection,
        };
        const vm = {
            [unix]: this.unixVm,
            [windows]: this.windowsVm,
        };
        const domainType = {
            [unix]: this.unixDomainType,
            [windows]: this.windowsDomainType,
        };

        const showErrorMsg = {
            [unix]: this.unixShowErrorMsg,
            [windows]: this.WindowsShowErrorMsg,
        };

        return {
            selection: selection[key],
            domainType: domainType[key],
            vm: vm[key],
            showErrorMsg: showErrorMsg[key],
            usrOsType: this.belongType === this.i18n.unixUserLab ? UserOsTypeE.Unix : UserOsTypeE.Windows,
        };
    }

    /**
     * ad域用户用户名格式校验
     */
    adNameFormatValidator(control: FormControl): ValidationErrors {
        if (!control.value) {
            return null;
        }
        const valueList = control.value.split('\\');
        const isValid = valueList.length === 2 && valueList.every((item) => item.trim());
        return isValid ? null : { nameFormat: true };
    }

    /**
     *  修改当前tab的错误信息
     */
    changeErrorMsg(value?: boolean): boolean {
        if (this.belongType === this.i18n.unixUserLab) {
            return this.unixShowErrorMsg = value === undefined ? !this.unixShowErrorMsg : value;
        } else if (this.belongType === this.i18n.windowsUserLab) {
            return this.WindowsShowErrorMsg = value === undefined ? !this.WindowsShowErrorMsg : value;
        } else {
            return undefined;
        }
    }

    /**
     * 校验
     */
    validateVm(): boolean {
        const { domainType, vm, selection } = this.getCurrentValue();
        if (domainType === DomainTypeE.Local) {
            if (!selection.length) {
                this.changeErrorMsg(true);
                return false;
            }
            return true;
        }
        vm.validate();
        return vm.valid;
    }

    onOk(): void {
        if (!this.validateVm()) {
            return;
        }
        const { domainType, vm, selection, usrOsType } = this.getCurrentValue();

        if (domainType === DomainTypeE.Local) {
            this.closeArgs.data = (selection as User[]).map((item: UnixLocalUser & WindowsLocalUser) => ({
                id: (item.id || item.RID) as string, // 底层返回的id实际为数字
                USRGRPOWNERNAME: item.name || (`localhost\\${item.NAME}`), // windows 本地用户需要添加 localhost\前缀，问就是需求
                USRGRPTYPE: UserOrGroupTypeE.Local,
                NAMETYPE: this.type === 'user' ? NameTypeE.User : NameTypeE.Group,
                DOMAINTYPE: domainType,
                usrOsType,
            }));
        } else {
            const names: string[] = vm.value.names;
            this.closeArgs.data = names.map((a) => {
                const item: UserOrGroup = {
                    USRGRPOWNERNAME: a,
                    USRGRPTYPE: UserOrGroupTypeE.Domain,
                    NAMETYPE: this.type === 'user' ? NameTypeE.User : NameTypeE.Group,
                    DOMAINTYPE: domainType,
                    usrOsType,
                };
                item.id = `${item.USRGRPOWNERNAME}-${item.USRGRPTYPE}-${item.NAMETYPE}-${item.DOMAINTYPE}`;

                return item;
            });
        }

        this.closeArgs.refresh = true;
        this.windowVisible = false;
    }
}
